import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Box, Divider, Flex, Grid, Label, Link, Spinner, Badge } from "theme-ui";
import { themevals } from "theme/themevals";
import { deleteProvider, getProvider, updateProvider, resyncProvider } from "api/Providers";
import ReloadIcon from "components/svg/icons/ReloadIcon";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { BackArrowIcon, PencilIcon, QuestionMarkIcon, TrashIcon, UsersIcon } from "components/svg";
import H2 from "components/typography/H2";
import H5 from "components/typography/H5";
import P from "components/typography/P";
import BasicModal from "components/ui/BasicModal";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import ProviderUsers  from "./components/ProviderUsers";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";

const Provider = () => {
  const [provider, setProvider] = useState<any>(null);
  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "users";
  const [activeTab, setActiveTab] = useState<string>(defaultTab);
  useEffect(() => {
    const tab = searchParams.get("tab") || "users";
    setActiveTab(tab);
  }, [searchParams]);
  const params = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useContext<any>(UserContext);
  const hasTrackedView = useRef(false);

  const init = async () => {
    const res = await getProvider({ data: { id: params.id } });
    setProvider(res?.id ? res : null);
  };

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Provider";
    hasTrackedView.current = false;
    init();
  }, [params.id]);

  useEffect(() => {
    if (!provider?.id || hasTrackedView.current) {
      return;
    }
    hasTrackedView.current = true;
    void trackActivity({
      module: "providers",
      action: "view_provider_details",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: "provider",
      entityId: provider.id,
      metadata: null,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [provider?.id, user?.id, user?.company?.id]);

  const submitSync = async () => {
    setConfirmSubmit(true);
    const res = await resyncProvider({ data: provider });
    if (res?.success) {
      await init(); // Refresh provider data
      toast.add("Provider synced.", "var(--theme-ui-colors-success)");
    } else {
      toast.add(res?.message ? res.message : "Error syncing provider.", "var(--theme-ui-colors-danger)");
    }
    setConfirmSubmit(false);
  };

  const confirmDelete = async () => {
    if (!provider?.id) return;
    setConfirmSubmit(true);
    try {
      const res = await deleteProvider({ data: provider });
      if (res?.success) {
        toast.add("Provider deactivated.", "var(--theme-ui-colors-success)");
        void trackActivity({
          module: "providers",
          action: "deactivate_provider",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "provider",
          entityId: provider.id,
          metadata: {
            source: "provider_detail_danger_zone",
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
        setConfirmModal(false);
        navigate("/providers");
      } else {
        toast.add(res?.message ? res.message : "Error deactivating provider.", "var(--theme-ui-colors-danger)");
      }
    } catch (error: any) {
      toast.add(error?.message ? error.message : "Error deactivating provider.", "var(--theme-ui-colors-danger)");
    } finally {
      setConfirmSubmit(false);
    }
  };

  const submitDelete = async () => {
    setConfirmModal(
      <BasicModal
        title="Deactivate client?"
        cancel_click={() => setConfirmModal(false)}
        cancel_text="No, cancel" 
        size={"sm"}
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        actions={
         
            <BasicButton type="button" scheme="danger" onClick={() => confirmDelete()} $submitting={confirmSubmit} $iconHeight="14px" $iconWidth="14px" $loadingColor="#000" keepText={true}>
              {!confirmSubmit && <TrashIcon fill="#fff" />}
              {"Deactivate"}
            </BasicButton>
        }
      >
        <P>
          Are you sure you want to deactivate <strong style={{ color: "var(--theme-ui-colors-black)" }}>{provider?.name}</strong>?
        </P>
      </BasicModal>
    );
  };

  const renderStatusBadge = (status: number) => {
    if (status === 1) {
      return (
        <Badge as="span" variant="success">
          Active
        </Badge>
      );
    }
    return (
      <Badge as="span" variant="danger">
        Inactive
      </Badge>
    );
  };

  const formatLastSync = (iso?: string) => {
    if (!iso) return 'N/A';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return 'N/A';
    const month = d.toLocaleString('en-US', { month: 'short' });
    const day = d.getDate();
    const year = d.getFullYear();
    const ordinal = (n: number) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    };
    let hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${month} ${day}${ordinal(day)}, ${year} ${hours}:${minutes} ${ampm}`;
  };

  const lastSyncSuccessful = provider?.external?.zoho?.lastSyncSuccessful;
  const lastSyncColor =
    lastSyncSuccessful === undefined
      ? themevals.colors.dkgrey
      : lastSyncSuccessful
      ? themevals.colors.success
      : themevals.colors.danger;
  const lastSyncText = provider?.external?.zoho?.lastSync
    ? formatLastSync(provider.external.zoho.lastSync)
    : 'N/A';

  return (
    <>
      <Header></Header>
      {!provider && (
        <Box sx={{ textAlign: "center", paddingTop: "30px" }}>
          <Spinner size={25} strokeWidth={3} title="loading" />
        </Box>
      )}
      {provider && (
        <Page
          backAction={
            <Link variant="back" href="/providers">
              <BackArrowIcon />
              Back to Providers
            </Link>
          }
        >
          <Flex sx={{ gap: "20px", alignItems: "center" }}>
            <H2>{provider.name}</H2>
          </Flex>
          <Grid gap={0} columns={[1, "250px 1fr"]}>
            <Box>
              <H5 marginTop={"30px"} marginBottom={"10px"} fontFamily={"Poppins"} color={themevals.colors.dkgrey} fontSize={"14px"} fontWeight={500}>
                Provider Details
              </H5>
            </Box>
            <Flex sx={{ paddingLeft: 30, alignItems: "flex-end" }}>
              <Flex sx={{ gap: "40px" }}>
                <Flex
                  sx={{ paddingBottom: "10px", borderBottom: activeTab == "users" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }}
                  onClick={() => {
                    setActiveTab("users");
                    setSearchParams({ tab: "users" });
                  }}
                >
                  <UsersIcon fill={activeTab == "users" ? "#000" : themevals.colors.dkgrey} width="14px" />
                  <Box sx={{ color: activeTab == "users" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                    Users
                  </Box>
                </Flex>
              </Flex>
            </Flex>
          </Grid>
          <Grid gap={0} columns={[1, "250px 1fr"]}>
            {/* See Projects/view.tsx: minWidth 0 + overflowWrap on the
                  sidebar column so emails and URLs wrap inside 250px. */}
              <Box sx={{ borderTop: "1px solid", borderRight: "1px solid", borderColor: "inputOutline", minWidth: 0, overflowWrap: "anywhere" }}>
              <Box sx={{ paddingRight: "30px", paddingTop: "20px" }}>
                <Link
                  href={`/providers/${provider.id}/edit`}
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                    width: "100%", padding: "10px 0",
                    border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px",
                    textDecoration: "none", color: themevals.colors.black, backgroundColor: "#fff",
                    fontFamily: "Poppins", fontSize: "13px", fontWeight: 500, cursor: "pointer",
                    "&:hover": { backgroundColor: "#F7F8FA", borderColor: themevals.colors.dkgrey },
                  }}
                >
                  <PencilIcon height="13px" fill="currentColor" />
                  Edit
                </Link>
              </Box>
              <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                Basic Info
              </H5>
              <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Status</Label>
                <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                  {renderStatusBadge(provider.status)}
                </P>
              </Flex>
              <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Contact Email</Label>
                <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                  {provider?.contactEmail}
                </P>
              </Flex>
              <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Billing Email</Label>
                <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                  {provider?.billingEmail ? provider?.billingEmail : "-"}
                </P>
              </Flex>
              <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
              <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                Address
              </H5>
              <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} lineHeight={"22px"} marginTop={"0px"} marginBottom={"30px"}>
                {[provider?.address?.line1, provider?.address?.line2].filter(Boolean).join(", ")}
                <br />
                {[provider?.address?.city, provider?.address?.state, provider?.address?.postalCode].filter(Boolean).join(" ")}
              </P>
              <Divider sx={{ color: themevals.colors.inputOutline, marginTop: "10px" }} />
              <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                Third-Party Sync
              </H5>
              <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Last Sync</Label>
                <P
                  fontFamily={"Poppins"}
                  color={lastSyncColor}
                  fontSize={"14px"}
                  fontWeight={600}
                  marginTop={"5px"}
                  marginBottom={"0px"}
                >
                  {lastSyncText}
                </P>
              </Flex>
              <BasicButton scheme="secondaryOutline" type="button" $iconHeight="14px" $iconWidth="14px" $loadingColor="#000" $submitting={confirmSubmit} keepText={true} onClick={() => submitSync()}>
                {!confirmSubmit && <ReloadIcon fill={"#000"} />}Resync
              </BasicButton>
              <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "30px" }} />
              <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                Danger Zone
              </H5>
              <BasicButton scheme="dangerSecondary" type="button" onClick={() => submitDelete()}>
                <TrashIcon fill={"#A60000"} /> Deactivate
              </BasicButton>
            </Box>
            <Box sx={{ paddingTop: "30px", paddingLeft: "30px", borderTop: "1px solid", borderColor: "inputOutline" }}>
              {activeTab === "users" && <ProviderUsers  provider={provider}/>}
            </Box>
          </Grid>
          {confirmModal}
        </Page>
      )}
    </>
  );
};

export default Provider;
